import {
  SHOWCASE,
  showcaseAnimatedPanels,
  itemSlots,
  PersonaImageCollection,
  type Animal,
  type Item,
  type ItemSlot,
  type Persona,
  type PersonaImageInfo,
  type WithId,
  type WithRef,
  wornItemIds,
  getOutfitBasePersona,
  getPrimaryPanelKey,
  getPrimarySheet,
  isApiManagedPersona,
  type ImageReference,
} from "@um/contracts";

export type Character = WithRef<Animal> & { id: string };

/** What a character is wearing, and what changing it costs. */

/** Items created before slots existed have to land somewhere. */
const DEFAULT_SLOT: ItemSlot = "body";

/**
 * The slot an item occupies, coerced to one that still exists. Rows written
 * against retired slots have no entry in the slot tables, and an undefined
 * `SLOT_LABELS[slot]` crashes the drop handler.
 */
export function slotOf(item: WithId<Item> | undefined): ItemSlot {
  const slot = item?.slot;
  return slot && itemSlots.includes(slot) ? slot : DEFAULT_SLOT;
}

/**
 * What to call a slot in the interface: retail categories, not anatomy. Mass
 * nouns, so they work without an article: "Order headwear for Pugsley".
 */
export const SLOT_LABELS: Record<ItemSlot, string> = {
  head: "Headwear",
  body: "Clothing",
  accessory: "Accessories",
};

/**
 * Where on the character a slot sits. Separate from `SLOT_LABELS` because
 * refusals name the place ("goes on the head"), headings want the category.
 */
export const SLOT_PLACES: Record<ItemSlot, string> = {
  head: "head",
  body: "body",
  accessory: "accessories",
};

/**
 * Showcase panel counts. Re-exported from `@um/contracts` because the backend
 * reads the same numbers; a local copy goes stale silently.
 */
const SHOWCASE_SEQUENCE_PANELS = showcaseAnimatedPanels;
export const SHOWCASE_REST_PANELS = SHOWCASE.restPanels;

/**
 * The pictures to use where the outfit is the subject: B1–B15 in panel order,
 * falling back to a single `A9` without a showcase sheet, or to the first pose
 * of the primary sheet for a version-3 character without an A sheet. A B sheet
 * drawn for a performance is never a showcase. Not for the crew card, chat, or
 * activity thumbnails, which want the close A framing at 64px.
 */
export function showcaseImages(
  persona: Persona | undefined
): PersonaImageInfo[] {
  if (!persona) return [];
  const images = new PersonaImageCollection(persona);
  // A performance sheet holds dance moves and a finale, not the outfit.
  const showcase = persona.imageVariations?.B?.purpose !== "performance";

  // All or nothing: `lookup` falls back to the sheet default for a missing
  // panel, so a partial read seeds the sequence with unrelated poses from older
  // 2×2 and 3×3 sheets.
  const complete =
    showcase &&
    Array.from(
      { length: SHOWCASE_SEQUENCE_PANELS },
      (_, i) => `B${i + 1}`
    ).every((id) => images.hasVariationId(id));

  if (complete) {
    return Array.from(
      { length: SHOWCASE_SEQUENCE_PANELS },
      (_, i) => images.lookup(`B${i + 1}`)!
    );
  }

  // B1 is framed on the outfit even when the sheet can't be animated.
  if (showcase && images.hasVariationId("B1")) return [images.lookup("B1")!];

  // A character drawn only for a performance shows its idle pose (panel 1 of
  // its primary sheet) until its A sheet exists. Older versions keep A9, whose
  // lookup falls back to their portrait.
  const fallback = images.lookup(
    !persona.imageVariations?.A && isApiManagedPersona(persona)
      ? getPrimaryPanelKey(persona)
      : "A9"
  );
  return fallback ? [fallback] : [];
}

/**
 * True for the one tier worth marking. Items written before rarity existed have
 * no field and count as standard, which matches how they were generated.
 */
export function isRare(item: WithId<Item> | undefined): boolean {
  return item?.rarity === "rare";
}

/** What to call a character on screen. */
export function characterName(character: Character | undefined): string {
  return currentPersona(character)?.name || character?.name || "your companion";
}

export function currentPersona(
  character: Character | undefined
): Persona | undefined {
  const personas = usablePersonas(character);
  return (
    personas.find((p) => p.id === character?.defaultPersonaId) ??
    personas[personas.length - 1]
  );
}

/** The most recent ordinary outfit attempt for the current character picture. */
export function latestOutfitAttempt(character: Character | undefined): Persona | undefined {
  const base = getOutfitBasePersona(character, currentPersona(character));
  if (!base) return undefined;
  return [...(character?.personas ?? [])].reverse().find(p =>
    !p.isDeleted && !p.isHidden && wornItemIds(p).length > 0 &&
    getOutfitBasePersona(character, p)?.id === base.id
  );
}

/**
 * Versions fit to show or build on. A failed version keeps an `image` carrying
 * the half-made picture, so "the newest persona" can otherwise render broken.
 */
function usablePersonas(character: Character | undefined): Persona[] {
  return (character?.personas ?? []).filter(
    (p) => !p.isDeleted && !hasFailed(p)
  );
}

// Canonical in `@um/contracts` so the admin portal counts wear the same way.
export { wornItemIds };

/**
 * A version whose generation failed. `onCreatePersonaTask` writes any of
 * `status: "error"`, `error`, or `errorCode`. Never show, reuse, or wait on one.
 */
export function hasFailed(persona: Persona): boolean {
  return persona.status === "error" || !!persona.error || !!persona.errorCode;
}

const hasArt = (image: ImageReference | undefined) =>
  !!(image?.original || image?.large || image?.small);

/**
 * True once the persona's expression sheet exists and it can be shown.
 *
 * A version-3 persona (made by the `create` API) may have no A sheet: it is
 * ready with whichever sheet it is drawn from, or with only its portrait so
 * far. Every other version is still drawn from its A sheet.
 */
export function isPersonaReady(persona: Persona): boolean {
  if (hasFailed(persona) || persona.status === "generating" || (persona.generationStage && persona.generationStage !== "complete")) return false;
  if (isApiManagedPersona(persona))
    return !!getPrimarySheet(persona) || hasArt(persona.image);
  return hasArt(persona.imageVariations?.A);
}

/**
 * A persona of the current character version wearing exactly this set, if one
 * exists. Clothing alone must not match an outfit made from an older version.
 */
export function personaWearing(
  character: Character | undefined,
  itemIds: string[]
): Persona | undefined {
  const want = [...itemIds].sort().join("|");
  const selected = currentPersona(character);
  const matches = (p: Persona) =>
    !p.isDeleted && isPersonaReady(p) && [...wornItemIds(p)].sort().join("|") === want;
  if (selected && matches(selected)) return selected;
  const base = getOutfitBasePersona(character, selected);
  if (!base) return undefined;
  return (character?.personas ?? []).find(
    (p) => matches(p) && getOutfitBasePersona(character, p)?.id === base.id
  );
}

/** The set that results from putting `item` on, replacing its slot's occupant. */
export function withItemEquipped(
  current: WithId<Item>[],
  item: WithId<Item>
): string[] {
  const slot = slotOf(item);
  return [...current.filter((worn) => slotOf(worn) !== slot), item].map(
    (i) => i.id
  );
}

export function withItemRemoved(
  current: WithId<Item>[],
  itemId: string
): string[] {
  return current.filter((worn) => worn.id !== itemId).map((i) => i.id);
}

export interface WearState {
  /** A finished persona wearing it is the character's current look. */
  worn: boolean;
  /** A look including it is still being drawn. */
  pending: boolean;
  character?: Character;
}

/**
 * Where an item stands across every character on the account. "Worn" means the
 * character's current look includes it, not that some old version does.
 */
export function itemWearState(
  itemId: string,
  characters: Character[]
): WearState {
  return wearStates(characters).get(itemId) ?? NOT_WORN;
}

const NOT_WORN: WearState = { worn: false, pending: false };

/**
 * Every item's wear state in one pass. Callers rendering a list should build
 * this once and look up; `itemWearState` re-walks every persona per item.
 */
export function wearStates(characters: Character[]): Map<string, WearState> {
  const states = new Map<string, WearState>();

  for (const character of characters) {
    // Same "in progress" rule as everywhere else, so a stale or failed version
    // can't mark an item pending forever.
    const generating = generatingLook(character);
    if (generating) {
      for (const id of character.outfit?.itemIds ?? []) {
        if (!states.has(id)) {
          states.set(id, { worn: false, pending: true, character });
        }
      }
    }

    // Worn beats pending, whatever some other unfinished look asks for.
    for (const id of wornItemIds(currentPersona(character))) {
      states.set(id, { worn: true, pending: false, character });
    }
  }

  return states;
}

/**
 * The look currently being drawn: the intended outfit. Slots must render from
 * this, not the current persona, or a change shows nothing for minutes.
 */
export function generatingLook(
  character: Character | undefined
): Persona | undefined {
  return character?.outfit
    ? character.personas?.find(p => p.id === character.outfit!.personaId)
    : undefined;
}

/** True while any look on this character is still being drawn. */
export function isCharacterChanging(character: Character | undefined): boolean {
  return !!generatingLook(character);
}

/** Set equality for item ids, order-insensitive. */
export function sameItemSet(a: string[], b: string[]): boolean {
  return (
    a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|")
  );
}
